import React, { useMemo, useState } from "react";
import { Plus, RefreshCw, ClipboardList, Pencil, Trash2 , StickyNote} from "lucide-react";
import SalesNoteFormModal from "./SalesNoteFormModal";

export default function SalesNotesView({
  loading,
  notes,
  clients,
  clientsMap,
  selectedClientId,
  setSelectedClientId,
  onRefresh,
  onCreate,
  onUpdate,
  onDelete,
}) {
  const [openModal, setOpenModal] = useState(false);
  const [editing, setEditing] = useState(null);

  const headers = useMemo(() => ["Client", "Note Content", "Outcome", "Actions"], []);

  const openCreate = () => {
    setEditing(null);
    setOpenModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditing(null);
  };

  const clientLabel = (clientId) => {
    const c = clientsMap?.get?.(String(clientId));
    if (!c) return `#${clientId}`;
    const name = c.companyName || "-";
    const person = `${c.firstName || ""} ${c.secondName || ""}`.trim();
    return person ? `${name} — ${person}` : name;
  };

  return (
    <div className="text-white">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-white/10 border border-white/20 backdrop-blur-xl">
            <StickyNote className="w-6 h-6 text-pink-200" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Pre-Sales Notes</h2>
            <p className="text-xs text-white/70">Capture outcomes and follow-ups</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-purple-600/80 hover:bg-purple-700 text-white flex items-center gap-2 backdrop-blur-lg"
          >
            <Plus className="w-5 h-5" />
            Add Note
          </button>

          <button
            onClick={onRefresh}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 text-white flex items-center gap-2 backdrop-blur-lg disabled:opacity-50"
            disabled={loading}
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="mb-5 backdrop-blur-xl bg-white/10 rounded-2xl border border-white/20 shadow-[0_0_30px_rgba(255,0,255,0.15)] p-4">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-sm text-gray-200">Filter by Client</label>
            <select value={selectedClientId} onChange={(e) => setSelectedClientId(e.target.value)} className={selectCls}>
              <option value="" className="text-black">
                All
              </option>
              {(clients || []).map((c) => (
                <option key={c.id} value={c.id} className="text-black">
                  {c.companyName || `Client #${c.id}`}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 text-sm text-gray-200">
            Showing: <span className="text-white font-semibold">{notes?.length || 0}</span> notes
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="backdrop-blur-xl bg-white/10 rounded-2xl shadow-[0_0_40px_rgba(255,0,255,0.20)] border border-white/20 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-pink-500/20 to-purple-600/30 backdrop-blur-xl flex items-center justify-between">
          <h3 className="text-lg font-bold">Notes List</h3>
          <span className="text-sm text-gray-200">Total: {notes?.length || 0}</span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-white">
            <thead className="bg-white/10">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-3 text-right text-sm font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-white/10">
              {(notes || []).map((n) => (
                <tr key={n.id} className="hover:bg-white/10 transition">
                  <td className="px-4 py-3 text-right">
                    <div className="font-semibold">{clientLabel(n.clientId)}</div>
                    <div className="text-xs text-gray-300">clientId: {n.clientId}</div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="text-sm whitespace-pre-wrap break-words max-w-[520px]">
                      {n.noteContent || "-"}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <span className="px-2 py-1 rounded text-xs bg-white/10 border border-white/20">
                      {n.outcome || "-"}
                    </span>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => openEdit(n)}
                        className="px-3 py-1.5 rounded-lg bg-pink-600/80 hover:bg-pink-700 text-white text-xs backdrop-blur-lg flex items-center gap-1"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </button>

                      <button
                        onClick={() => onDelete?.(n.id)}
                        className="px-3 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-100 text-xs backdrop-blur-lg flex items-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!loading && (!notes || notes.length === 0) && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                    No notes yet.
                  </td>
                </tr>
              )}

              {loading && (
                <tr>
                  <td className="px-4 py-10 text-center text-gray-200" colSpan={4}>
                    Loading...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {openModal && (
        <SalesNoteFormModal
          clients={clients || []}
          initialData={editing}
          onClose={closeModal}
          onSubmit={async (payload) => {
            if (editing?.id) {
              const ok = await onUpdate?.(editing.id, payload);
              if (ok) closeModal();
            } else {
              const ok = await onCreate?.(payload);
              if (ok) closeModal();
            }
          }}
        />
      )}
    </div>
  );
}

const selectCls =
  "mt-2 w-full px-3 py-2 bg-white/10 text-white border border-white/20 rounded-lg outline-none focus:ring-2 focus:ring-pink-400/30";
